// eslint-disable-next-line no-unused-vars
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';
import { useSWRConfig } from 'swr';
import { stringify } from 'query-string';

import useFetch from 'Hooks/useFetch';
import useCart from 'Hooks/useCart';
import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';
import useProduct from 'Hooks/useProduct';
import useWishlists from 'Hooks/useWishlists';
import useNumberUtils from 'Hooks/useNumberUtils';
import ProductComponent from 'Components/Product/Product';
import useAnalytics from 'Hooks/useAnalytics';
import { API_PRODUCTS, PROMOTION_BNDL, API_SUPPLIER_PRODUCT_LIST, CURRENCY_RON } from 'Constants';
import { defaultModalsState, ModalsContext } from 'Contexts/ModalsContext';
import { ToastNotificationsContext } from 'Contexts/ToastNotificationsContext';
import ProductAbstract from 'Containers/ProductAbstract';
import { stockAlertWishlistId } from 'Contexts/WishlistContext';

const ProductContainer = memo(() => {
  const fetch = useFetch();
  const cart = useCart();
  const { state: linkState } = useRouter();
  const { data: user } = useUser();
  const wishlistContext = useWishlists();
  const { formatNumber, formatDate } = useNumberUtils();
  const addNotification = useContextSelector(ToastNotificationsContext, data => data.addNotification);
  const { cache } = useSWRConfig();
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { data: product, recommendedLoading, error, isLoading, updateProduct } = useProduct();
  const [nodes, setNodes] = useState({});
  const [promoAddToCartLoading, setPromoAddToCartLoading] = useState(false);
  const [addToCartResealedLoading, setAddToCartResealedLoading] = useState(false);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [addToWishlistLoading, setAddToWishlistLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadedProduct, setLoadedProduct] = useState({});
  const stateProduct = product || {};
  if ('promotions' in stateProduct) {
    stateProduct.promotions = ProductAbstract.composePromotions(product);
  }
  const [contextProduct, setCtxProduct] = useState(stateProduct);
  const carouselRef = useRef(null);
  const setModalState = useContextSelector(ModalsContext, data => data.setState);
  const quickViewModalState = useContextSelector(ModalsContext, data => data.modalState.QUICK_VIEW.state);
  const [canShow, setShow] = useState(cache.get('serverSide'));
  const [isDescOpen, setDescOpen] = useState(false);
  const [selectedSlide, onSelectSlide] = useState(null);
  const [isRefreshButtonEnabled, setRefreshButtonState] = useState(true);
  const [supplierProductList, setSupplierProductList] = useState({});
  const [supplierProductListLoading, setSupplierProductListLoading] = useState(true);

  const timeoutRef = useRef(null);
  const measureTimeoutRef = useRef(null);
  const hasTrackedRef = useRef(false);
  const initialLinkState = useRef(linkState);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isLoading || hasTrackedRef.current || !product?.id) {
      return;
    }

    hasTrackedRef.current = true;

    const source = initialLinkState.current?.source || 'direct';

    const getInventorySource = () => {
      if (!product?.hasPrice) return 'supplier';
      if (product?.hasSupplierOffers) return 'mixed';
      return 'local';
    };

    analytics.track('product_viewed', {
      product_id: product?.id,
      product_sku: product?.code,
      source,
      inventorySource: getInventorySource(),
      ...initialLinkState.current,
    });
  }, [
    isLoading,
    product?.id,
    product?.hasPrice,
    product?.hasSupplierOffers
  ]);

  /**
   * Open modal with pictures
   * @param idx
   */
  const handleOnClickSlideItem = idx => {
    onSelectSlide(idx);
    if (product?.pictures?.length) {
      setModalState(defaultModalsState.PRODUCT_PICTURES.key, {
        open: true,
        pictures: product.pictures,
        selected: idx
      });
    }
  };

  /**
   * Add promotion product to cart
   * @param {object} promoProduct
   * @returns {Promise<*|null>}
   */
  const handleOnAddPromoToCart = async promoProduct => {
    const { quantity } = promoProduct;
    const id = product.id || product.productId;
    const promotionId = promoProduct.promotionId || promoProduct.promotion?.promotionId;
    const payload = {
      id,
      stockValue: promoProduct.stockValue,
      quantity,
      minQuantity: promoProduct.minQuantity,
      promotion: {
        promotionId
      }
    };
    const existingProd = cart.data?.items?.find(item => {
      if (
        promotionId === item?.promotion?.promotionId
        && item.promotion.type === PROMOTION_BNDL
      ) {
        return (
          id === item.productId
          || !!item
            .promoItems[promotionId]
            ?.find(promoItem => promoItem.productId === id)
        );
      }

      return (
        item.productId === id
        && (item.promotionId || item.promotion?.promotionId) === promotionId
      );
    });
    if (existingProd) {
      payload.id = existingProd.productId;
      payload.quantity += ((existingProd.quantity / existingProd.minQuantity) * payload.minQuantity);
    }
    if (payload.quantity > promoProduct.stockValue) {
      addNotification({
        type: 'warning',
        title: t('products.insufficientStock')
      });
      return null;
    }
    try {
      setPromoAddToCartLoading(promotionId);
      const response = await cart.updateProduct(payload);
      addNotification({
        title: t('cart.successfullyAddedPromotionToCart')
      });
      return response;
    } catch (err) {
      addNotification({
        title: t('cart.failedAddingPromotionToCart'),
        type: 'error'
      });
    } finally {
      setPromoAddToCartLoading(false);
    }
    return null;
  };
  /**
   * Handle adding product, promos or resealed products to cart
   * @param {object} productToAdd
   * @returns {Promise<void>}
   */
  const handleOnAddToCart = productToAdd => async () => {
    if (!productToAdd.hasPrice) {
      setModalState(defaultModalsState.ADD_EXTERNAL_SUPPLIER_PRODUCT_TO_CART.key, {
        open: true,
        product: productToAdd,
        onConfirm: () => executeAddToCart(productToAdd)
      });
      return null;
    }

    return executeAddToCart(productToAdd);
  };

  /**
   * Execute the actual add to cart logic
   * @param {object} productToAdd
   * @returns {Promise<void>}
   */
  const executeAddToCart = async productToAdd => {
    const { productId, isResealedVariant, stockValue } = productToAdd;
    let { minQuantity, quantity } = productToAdd;
    const id = productId || productToAdd.id;
    if (contextProduct.productId === productToAdd.productId) {
      quantity = contextProduct.quantity;
      minQuantity = contextProduct.minQuantity;
    }
    try {
      const existingProd = cart.data?.items?.find(item => (
        item.productId === id
        && item?.supplierId === productToAdd.supplierId
        && !item.promotion
      ));
      if ((existingProd?.quantity || 0) + quantity > stockValue) {
        addNotification({
          type: 'warning',
          title: t('products.insufficientStock')
        });
        return null;
      }
      if (isResealedVariant) {
        setAddToCartResealedLoading(id);
      } else {
        setAddToCartLoading(true);
      }
      const [updated, crossProducts] = await Promise.allSettled([
        cart.updateProduct({
          ...productToAdd,
          id,
          quantity: (existingProd?.quantity || 0) + quantity,
          minQuantity,
          ...(!productToAdd.hasPrice && {
            supplierId: productToAdd.supplierId,
            supplierOfferId: productToAdd.supplierOfferId,
          })
        }),
        fetch(`${ API_PRODUCTS }/${ product.id }/cross-sells?currency=${ user?.defaultCurrency }&vat=${ user?.withVat }`)
      ]);
      if (updated.status === 'fulfilled') {
        if (updated.value === null) {
          addNotification({
            type: 'warning',
            title: t('products.dropShipmentWarning')
          });
        } else {
          addNotification({
            title: t('cart.successfullyAddedToCart')
          });
          if (!isResealedVariant) {
            setCtxProduct(prod => ({
              ...prod,
              quantity: 1
            }));
          }
        }
      } else {
        addNotification({
          type: 'error',
          title: t('cart.failedAddToCart'),
          message: updated.value?.errors?.at(0)?.message
        });
        return null;
      }
      if (
        Array.isArray(crossProducts.value)
        && crossProducts.value.length
      ) {
        setModalState(defaultModalsState.CROSS_SALES.key, {
          open: true,
          products: crossProducts.value,
          product: productToAdd
        });
      }
      return updated.value;
    } catch (err) {
      addNotification({
        type: 'error',
        title: t('cart.failedAddToCart'),
        message: err?.info?.errors?.at(0)?.message
      });
    } finally {
      setAddToCartResealedLoading(false);
      setAddToCartLoading(false);
    }
    return null;
  };

  /**
   * Refresh product price
   * @returns {Promise<void>}
   */
  const handleOnRefreshPrice = async () => {
    try {
      setRefreshing(true);
      const data = await fetch(`${ API_PRODUCTS }/${ product.id }/refresh?currency=${ user?.defaultCurrency }&vat=${ user.withVat }`, { method: 'POST' });
      if (data?.id) {
        if (cart.data.items.find(item => item.productId === data.id && !item.promotion)) {
          cart.refresh();
        }
        setRefreshButtonState(false);
        timeoutRef.current = setTimeout(() => {
          setRefreshButtonState(true);
        }, 60 * 1000);
        updateProduct({
          ...product,
          price: data.price,
          promoPrice: data.promoPrice,
          ronPrice: data.ronPrice,
          ronPromoPrice: data.ronPromoPrice,
          catalogPrice: data.catalogPrice,
          stockValue: data.stockValue,
          minQuantity: data.minQuantity
        });
        addNotification({
          title: t('product.priceUpdateSuccess')
        });
      } else {
        addNotification({
          type: 'warning',
          title: t('product.priceUpdateFail')
        });
      }
    } catch (err) {
      addNotification({
        type: 'error',
        title: err.info?.errors?.at(0)?.message || t('product.priceUpdateFail')
      });
    } finally {
      setRefreshing(false);
    }
  };

  /**
   * Scroll to a dom element
   * @param {string} elementId
   * @returns {(function(*): void)|*}
   */
  const handleOnScrollTo = elementId => e => {
    e.preventDefault();
    if (typeof document !== 'undefined') {
      const node = nodes[elementId] || document.getElementById(elementId);
      if (node) {
        if (!(elementId in nodes)) {
          setNodes(prevNodes => ({
            ...prevNodes,
            [elementId]: node
          }));
        }
        window.scrollTo({
          top: node.offsetTop - 135,
          left: 0,
          behavior: 'smooth'
        });
      }
    }
  };

  /**
   * Quick view product
   * @param {object} evt
   * @returns {Promise<void>}
   */
  const handleOnQuickView = useCallback(
    async evt => {
      let prod = evt.id ? evt : product;
      let pricePayload = {
        ronPrice: prod.ronPrice,
        ronPromoPrice: prod.ronPromoPrice,
        price: prod.price,
        promoPrice: prod.promoPrice,
        catalogPrice: prod.catalogPrice,
        totalPrice: prod.totalPrice,
        ronTotalPrice: prod.ronTotalPrice
      };

      if (prod.promoItem) {
        if (prod.id === product.id) {
          prod = product;
        } else {
          pricePayload = {};
          prod = omit(
            prod,
            ['minQuantity', 'quantity', 'price', 'ronPrice', 'promoPrice', 'ronPromoPrice']
          );
          if (loadedProduct[prod.id]) {
            loadedProduct[prod.id] = omit(
              loadedProduct[prod.id],
              ['minQuantity', 'quantity', 'price', 'ronPrice', 'promoPrice', 'ronPromoPrice']
            );
          }
        }
      }
      const payload = { product: { ...prod } };

      setModalState(defaultModalsState.QUICK_VIEW.key, {
        open: true,
        product: {
          ...payload.product,
          ...(loadedProduct[prod.id] || {}),
          ...pricePayload
        }
      });

      if (
        loadedProduct
        && !prod.promoItem
        && prod.id in loadedProduct
        && loadedProduct[prod.id].detailsLoaded
        && loadedProduct[prod.id].price === prod.price
      ) {
        return;
      }

      try {
        const data = await fetch(`${ API_PRODUCTS }/${ prod.id }/quick-view?currency=${ user?.defaultCurrency }&vat=${ user?.withVat }`);
        const updatedPayload = {
          ...payload.product,
          ...data,
          quantity: Math.max(data.minQuantity, 1),
          detailsLoaded: true
        };
        setLoadedProduct(prevLoaded => ({
          ...prevLoaded,
          [prod.id]: updatedPayload
        }));
        // if (quickViewModalState.open) { // quickViewModalState Does not update
        setModalState(defaultModalsState.QUICK_VIEW.key, {
          product: updatedPayload
        });
        // }
      } catch (err) {
        if (quickViewModalState.state) {
          setModalState(defaultModalsState.QUICK_VIEW.key, {
            ...payload,
            error: err.message
          });
        }
        addNotification({
          type: 'error',
          message: t('products.unableToFetchQuickView')
        });
      }
    },
    [product, loadedProduct]
  );

  /**
   * Change product quantity
   * @param {number} qty
   */
  const handleOnChangeQuantity = qty => {
    const newQty = Math.min(
      product.stockValue,
      Math.max(qty, product.minQuantity || 1)
    );
    setCtxProduct(prod => {
      if (newQty !== prod.quantity) {
        return {
          ...prod,
          quantity: newQty
        };
      }
      return prod;
    });
  };

  /**
   * Add Product to desired wishlist
   * @param {object} selectedItem
   * @returns {Promise<*|null>}
   */
  const handleOnAddToWishlist = async ({ selectedItem = null }) => {
    const listId = selectedItem?.id || wishlistContext.defaultWishlist?.id;
    if (!listId || selectedItem?.createButton) return null;
    let existingWishlist = wishlistContext?.data[listId] || { products: [] };
    if (listId === wishlistContext.stockAlertWishlist.id) {
      existingWishlist = wishlistContext.stockAlertWishlist;
    }
    const existingProduct = (existingWishlist.products || []).find(prod => prod.id === product.id);
    if (existingProduct) {
      addNotification({
        type: 'warning',
        title: t('wishlists.productAlreadyExists')
      });
      return null;
    }
    setAddToWishlistLoading(true);
    try {
      const r = await wishlistContext.updateProduct(listId, {
        id: product.id,
        quantity: product.quantity || product.minQuantity || 1
      });
      if (r.errors) {
        addNotification({
          type: 'error',
          title: r.errors.at(0)?.message || t('wishlists.failedAddToWishlist')
        });
      } else {
        addNotification({
          title: listId === stockAlertWishlistId
            ? t('wishlist.addedToStockAlert')
            : t('wishlists.successfullyAddedToWishlist')
        });
      }
      return r;
    } catch (err) {
      addNotification({
        type: 'error',
        title: err.info?.errors?.at(0)?.message || t('wishlists.failedAddToWishlist')
      });
    } finally {
      setAddToWishlistLoading(false);
    }
    return null;
  };

  const handleOnToggleDesc = () => {
    setDescOpen(prevOpen => !prevOpen);
  };

  const handleStockAlertModalOpen = () => {
    setModalState(defaultModalsState.STOCK_ALERT.key, {
      open: true,
      productId: product.id || product.productId,
      productCode: product.code,
      productQuantity: Math.max(product.quantity, product.minQuantity)
    });
  };

  const measuredRef = useCallback(node => {
    if (measureTimeoutRef.current) {
      clearTimeout(measureTimeoutRef.current);
    }

    if (node !== null && (product?.longDescription?.length || product?.description?.length)) {
      measureTimeoutRef.current = setTimeout(() => {
        setShow(node?.scrollHeight > 300);
      }, 500);
    }
  }, [product?.longDescription, product?.description]);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (measureTimeoutRef.current) {
        clearTimeout(measureTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (contextProduct?.id !== product?.id) {
      setCtxProduct({});
      carouselRef?.current?.goToSlide(1, true);
    }
  }, [product?.id]);

  useEffect(() => {
    if (product) {
      const payload = {
        ...product,
        quantity: product.quantity || product.minQuantity || 1
      };
      if (product.stockValue !== contextProduct.stockValue) {
        payload.quantity = Math.min(payload.quantity, payload.stockValue);
      }
      if (
        product.recommended?.length
        && !contextProduct.recommended?.length
      ) {
        payload.recommended = product.recommended;
      }
      if (
        product.resealed?.length
        && !contextProduct.resealed?.length
      ) {
        payload.resealed = product.resealed;
      } else if (!product.hasResealed) {
        payload.hasResealed = false;
      }
      if (product.appliedPromotions?.length) {
        payload.promotions = ProductAbstract.composePromotions(product);
      }
      setCtxProduct(payload);
    } else {
      setCtxProduct({});
    }
  }, [product]);

  const handleSupplierProductList = cmp => {
    setSupplierProductListLoading(true);
    if (!cmp) {
      setSupplierProductList({});
    }
    const queryParams = {
      code: product?.code,
      ...(cmp && { 'show-cmp': 1 }),
    };
    fetch(`${ API_SUPPLIER_PRODUCT_LIST }?${ stringify(queryParams) }`)
      .then(res => {
        const { suppliers, ...rest } = res;
        const parsedSuppliers = (suppliers || []).map(item => ({
          id: item?.code,
          supplier: item?.name,
          pnSupplier: item?.product?.mpn,
          productEan: item?.product?.ean,
          price: formatNumber(item?.product?.price?.amount, { currency: item?.product?.price?.currency }),
          ronPriceWithVat: formatNumber(item?.product?.price?.amountRonWithTax, { currency: CURRENCY_RON }),
          ronPrice: formatNumber(item?.product?.price?.amountRon, { currency: CURRENCY_RON }),
          ...((cmp && item?.product?.price?.cmpRon) && { cmp: formatNumber(item?.product?.price?.cmpRon, { currency: CURRENCY_RON }) }),
          stock: item?.product?.stock,
          gmPercentage: `${item?.product?.price?.gmPercentage} %`,
          syncedAt: formatDate(item?.syncedAt)
        }));

        setSupplierProductList({ ...rest, suppliers: parsedSuppliers });
      }).finally(() => setSupplierProductListLoading(false));
  };

  useEffect(() => {
    if (product?.code && user?.isImpersonated && user?.hasAccessToSupplierProductOffers) {
      handleSupplierProductList();
    }
  }, [
    product?.code,
    user?.isImpersonated,
    user?.hasAccessToSupplierProductOffers
  ]);

  const showNodStockAlert = contextProduct?.hasPrice === false
    && contextProduct.supplierStockValue > 0
    && new Date(contextProduct.supplierStockDeliveryDate?.replace(' ', 'T')).setHours(0, 0, 0, 0)
      > new Date().setHours(0, 0, 0, 0);

  return (
    <ProductComponent
      product={ contextProduct }
      showNodStockAlert={ showNodStockAlert }
      carouselRef={ carouselRef }
      descElRef={ measuredRef }
      addToCartResealedLoading={ addToCartResealedLoading }
      addToCartLoading={ addToCartLoading }
      recommendedLoading={ recommendedLoading }
      isRefreshing={ refreshing }
      isLoading={ isLoading }
      onOpenDescription={ handleOnToggleDesc }
      selectedSlide={ selectedSlide }
      canShowDescButton={ canShow }
      isDescOpen={ isDescOpen }
      isRefreshButtonEnabled={ isRefreshButtonEnabled }
      error={ error?.message || error }
      promoAddToCartLoading={ promoAddToCartLoading }
      addToWishlistLoading={ addToWishlistLoading }
      onAddPromoToCart={ handleOnAddPromoToCart }
      onAddToCart={ handleOnAddToCart }
      onPriceRefresh={ handleOnRefreshPrice }
      onScrollTo={ handleOnScrollTo }
      onQuickView={ handleOnQuickView }
      onChangeQuantity={ handleOnChangeQuantity }
      onAddToWishlist={ handleOnAddToWishlist }
      onStockAlertModalOpen={ handleStockAlertModalOpen }
      onClickSlideItem={ handleOnClickSlideItem }
      onSelectSlide={ onSelectSlide }
      supplierProductList={ supplierProductList }
      supplierProductListLoading={ supplierProductListLoading }
      onSupplierProductList={ handleSupplierProductList }
    />
  );
});

ProductContainer.displayName = 'ProductContainer';

export default ProductContainer;
